import React, { useEffect, useState } from 'react';
import { api, uploadUrl } from '../../utils/api';
import { CLIENT_CATEGORIES } from '../../constants/clientCategories';
import ModalOverlay from '../../components/ModalOverlay';
import './Admin.css';

const CATEGORIES = CLIENT_CATEGORIES;
const EMPTY = { name: '', category: CATEGORIES[0], logo_filename: '' };

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoFileName, setLogoFileName] = useState('');

  const load = () => api.getClients().then(setClients).catch(() => {});
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(EMPTY); setLogoFileName(''); setError(''); setModal('create'); };
  const openEdit = (c) => {
    setForm({ name: c.name, category: c.category, logo_filename: c.logo_filename || '' });
    setLogoFileName(c.logo_filename || '');
    setError('');
    setModal(c.id);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (modal === 'create') await api.createClient(form);
      else await api.updateClient(modal, form);
      setModal(null);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    await api.deleteClient(id);
    load();
  };

  const handleLogoFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFileName(file.name);
    setUploading(true);
    setError('');
    try {
      const { filename } = await api.uploadClientLogo(file);
      setForm((f) => ({ ...f, logo_filename: filename }));
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const grouped = CATEGORIES.reduce((acc, cat) => {
    acc[cat] = clients.filter((c) => c.category === cat);
    return acc;
  }, {});

  return (
    <div>
      <div className="admin-page-header">
        <div className="admin-page-title">고객사 관리</div>
        <div className="admin-page-sub">주요 고객사 목록을 관리합니다. 로고 이미지를 직접 업로드할 수 있습니다.</div>
      </div>

      <div className="admin-toolbar" style={{ marginBottom: 16 }}>
        <span style={{ color: '#64748b', fontSize: '0.875rem' }}>총 {clients.length}개</span>
        <button className="btn btn-primary" onClick={openCreate}>+ 고객사 추가</button>
      </div>

      {CATEGORIES.map((cat) => (
        <div className="admin-card" key={cat}>
          <div style={{ fontWeight: 700, color: '#1e293b', marginBottom: 12 }}>{cat}</div>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>순서</th>
                  <th>기관명</th>
                  <th>작업</th>
                </tr>
              </thead>
              <tbody>
                {grouped[cat].length === 0 && (
                  <tr><td colSpan={3} style={{ textAlign: 'center', color: '#64748b' }}>없음</td></tr>
                )}
                {grouped[cat].map((c, i) => (
                  <tr key={c.id}>
                    <td>{i + 1}</td>
                    <td>{c.name}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => openEdit(c)}>수정</button>
                      {' '}
                      <button className="btn btn-danger btn-sm" onClick={() => remove(c.id)}>삭제</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {modal !== null && (
        <ModalOverlay onClose={() => setModal(null)}>
          <div className="modal-title">{modal === 'create' ? '고객사 추가' : '고객사 수정'}</div>
            <form onSubmit={save}>
              <div className="form-group">
                <label className="form-label">기관명</label>
                <input className="form-input" type="text" value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">분류</label>
                <select className="form-select" value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">로고 이미지</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 100, height: 52, flexShrink: 0, display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    border: '1px solid #e2e8f0', borderRadius: 6, background: '#f8fafc',
                  }}>
                    {form.logo_filename ? (
                      <img src={uploadUrl(form.logo_filename)} alt="로고 미리보기"
                        style={{ maxHeight: 40, maxWidth: 88, objectFit: 'contain' }} />
                    ) : (
                      <span style={{ color: '#cbd5e1', fontSize: '0.7rem' }}>없음</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 0 }}>
                    <label htmlFor="client-logo-file" className="btn btn-secondary btn-sm"
                      style={{ cursor: 'pointer', width: 'fit-content' }}>
                      파일 선택
                    </label>
                    <input id="client-logo-file" type="file" style={{ display: 'none' }}
                      accept="image/png,image/jpeg,image/svg+xml,image/webp"
                      onChange={handleLogoFile} disabled={uploading} />
                    <span style={{
                      fontSize: '0.8rem', color: '#64748b',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {logoFileName || '선택된 파일 없음'}
                    </span>
                  </div>
                </div>
                {uploading && <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 4 }}>업로드 중...</div>}
              </div>
              {error && <div className="form-error">{error}</div>}
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(null)}>취소</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? '저장 중...' : '저장'}
                </button>
              </div>
            </form>
        </ModalOverlay>
      )}
    </div>
  );
}
